// web/src/pages/StaffShell.tsx — המסגרת של עתידה (D-245).
// 🔑 הכלים לפי האפיון (7 קבצים ב-screen_spec). פעיל רק מה שנבנה; השאר מסומן "בקרוב" ⛔ קישור מת.
// 🆕 28.09 — "הודעות" (MS-04) עם מונה (MS-10): אצל הצוות המונה הוא מטלה. ⛔ אפס — הרכיב נעלם, ⛔ לא "0".
// 🆕 28.09 — "השולחן" (§14.23): רק למי שיש לה שולחן (בעלת הדייר · מנהלת-על — השרת מחליט, 404 לאחרות).
//    + כפתור "הערה לשיפור" בכל מסך של הצוות.
// 🆕 29.09 — תפארת, העוזרת של הצוות, במקום הכפתור: אותו מקום בפינה, חלון אחד — סיור במסך · הערה למוטי.
//    אם תפארת לא זמינה (כבויה / השרת לא ענה) — חוזר כפתור ההערות הרגיל.
import { useEffect, useState } from "react";
import { Navigate, NavLink, Outlet, useLocation } from "react-router-dom";
import { dk, ms } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { NetBanner } from "../components/NetBanner";
import { Tiferet } from "../tiferet/Tiferet";

const TOOLS: { to: string; label: string; ready: boolean }[] = [
  { to: "/calendar", label: "יומן", ready: true },
  { to: "/people", label: "לקוחות", ready: true },           // 🆕 27.09 — CR-01 · 02 · 03/04 · 06 · 07
  { to: "/overview", label: "העסק", ready: true },       // 🆕 03.10 — ב7 מה קורה בעסק
  { to: "/inbox", label: "הודעות", ready: true },             // 🆕 28.09 — MS-04 · 05
  { to: "/studio", label: "סטודיו", ready: true },            // 🆕 27.09 — ST-01 · 09 · 10 · 12–15 · 17 · 18
  { to: "/products", label: "מוצרים", ready: true },     // 🆕 04.10 — WP-05
  { to: "/marketing", label: "דיוור", ready: true },   // 🆕 03.10 — ב3 ניוזלטר · ב1 הגדרות המיילים (מתוך המסך)
  { to: "/testimonials", label: "המלצות", ready: true },   // 🆕 06.10 — HM-10 · 11 · 12 (הגירה 140)
  { to: "/affiliates", label: "שותפות", ready: true },   // 🆕 01.10 — פעימה א
];

/** 🆕 28.09 — מנחה שותפה (D-220): רואה רק את הסטודיו, ובו רק את הקורסים שלה (השרת מסנן). ⛔ יומן · לקוחות. */
export const isFacilitator = (role: string | null | undefined) => role === "facilitator";

function usePolled(load: () => Promise<number | null>) {
  const [n, setN] = useState<number | null>(null);
  const loc = useLocation();
  useEffect(() => {
    let alive = true;
    const run = () => load().then((v) => { if (alive) setN(v); }).catch(() => undefined);
    void run();
    const t = window.setInterval(run, 60_000);
    return () => { alive = false; window.clearInterval(t); };
  }, [loc.pathname, load]);
  return n;
}

const inboxCount = () => ms.GET("/staff/inbox/unread-count").then(({ data }) => data?.count ?? 0);
/** null = אין שולחן למשתמשת הזו (404) — הכלי לא מוצג בכלל. */
const deskCount = () => dk.GET("/desk/count").then(({ data }) => (data ? data.count : null));

function Count({ n, label }: { n: number | null; label: string }) {
  return n && n > 0 ? <span className="count" aria-label={`${n} ${label}`}>{n}</span> : null;
}

export function StaffShell() {
  const { signOut, state } = useAuth();
  const fac = state.status === "signedIn" && !state.me.is_staff && isFacilitator(state.me.role);
  const tools = fac ? TOOLS.filter((t) => t.to === "/studio") : TOOLS;
  const inbox = usePolled(inboxCount);
  const desk = usePolled(deskCount);
  return (
    <div className="shell">
      <header className="topbar">
        <span className="brand">עתידה</span>
        <nav className="tools" aria-label="כלים">
          {!fac && desk !== null && (
            <NavLink to="/desk" className="tool">השולחן<Count n={desk} label="מחכים לך" /></NavLink>
          )}
          {tools.map((t) =>
            t.ready ? (
              <NavLink key={t.to} to={t.to} className="tool">
                {t.label}{t.to === "/inbox" && <Count n={inbox} label="ממתינות לתשובה" />}
              </NavLink>
            ) : (
              <span key={t.to} className="tool tool-soon" aria-disabled="true" title="בקרוב">{t.label}</span>
            ),
          )}
        </nav>
        <button className="btn btn-quiet" onClick={() => void signOut()}>יציאה</button>
      </header>
      <NetBanner />
      <main className="page"><Outlet /></main>
      <Tiferet limited={fac} />
    </div>
  );
}

/** 🆕 28.09 — הכניסה של הצוות: מי שיש לה שולחן נוחתת בו; השאר — ביומן. */
export function StaffHome() {
  const [to, setTo] = useState<string | null>(null);
  useEffect(() => {
    deskCount().then((n) => setTo(n === null ? "/calendar" : "/desk")).catch(() => setTo("/calendar"));
  }, []);
  return to ? <Navigate to={to} replace /> : <p className="muted" style={{ padding: 16 }}>רגע…</p>;
}
